import positions from '@/data/country-coordinates.json';
import { styled } from '@/styles/stitches.config';
import { countryIdToName } from '@/utils/country-id-to-name';
import { useMediaQuery } from '@/utils/use-media-query';
import Portal from '@reach/portal';
import React, {
  FocusEvent,
  MouseEvent,
  MouseEventHandler,
  PointerEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { isDefined } from 'ts-is-present';

type PointsProps<T> = {
  getId: (d: T) => CountryIsoCode;
  getTooltip?: (context: TooltipContext<CountryCoordinate & T>) => React.ReactNode;
  hasThemeColors?: boolean;
  inStory?: boolean;
  // injected in WorldMap, do not add yourself
  isCenterTile?: boolean;
  // injected in WorldMap, do not add yourself
  tileSize?: number;
  data: T[];
};

type TooltipContext<T> = {
  data: T;
  root: Element;
};

export function Points<T>(props: PointsProps<T>) {
  const { data, getId, getTooltip, hasThemeColors, inStory, isCenterTile, tileSize = 480 } = props;

  const points: (CountryCoordinate & T)[] | undefined = useMemo(() => {
    if (isDefined(data)) {
      return data
        .map((d) => {
          const p = positions.find((p) => getId(d) === p.id);
          if (isDefined(p)) return { ...d, ...p };
        })
        .filter(isDefined);
    }
  }, [data, getId]);

  const [tooltipContext, setTooltipContext] = useState<TooltipContext<CountryCoordinate & T>>();
  const [isFocused, setIsFocused] = useState(false);
  const isTouch = useMediaQuery('(pointer: coarse)');

  // hide the tooltip on pointerleave or blur, but only if the last event wasn't focus
  const hideTooltip = useCallback(
    (e: FocusEvent | PointerEvent | MouseEvent, d: CountryCoordinate & T) => {
      if (e.type === 'blur') {
        if (tooltipContext?.data && getId(d) === getId(tooltipContext?.data)) {
          // if this is a blur event and the current id is not the id of this tooltip
          // disable focus mode and clear the tooltip
          setTooltipContext(undefined);
          setIsFocused(false);
        }
      } else {
        // if we are in focus mode, don't clear the tooltip context
        if (isFocused) return;

        // clear the tooltip context
        setTooltipContext(undefined);
      }
    },
    [getId, isFocused, tooltipContext?.data]
  );

  const showTooltip = useCallback(
    (e: FocusEvent | PointerEvent, d: CountryCoordinate & T) => {
      e.preventDefault();

      // if we are in focus mode, don't update the tooltip until it was blurred
      if (isFocused) return;

      // if we are already showing this tooltip, don't update the tooltip
      if (tooltipContext?.data && getId(tooltipContext?.data) === getId(d)) {
        if (!isTouch) {
          setIsFocused(true);
        }

        return;
      }

      // if this is focus mode, set isFocused to true
      if (e.type === 'focus') setIsFocused(true);

      // set the tooltip context
      setTooltipContext({ root: e.currentTarget, data: d });
    },
    [getId, isFocused, isTouch, tooltipContext?.data]
  );

  // r in % of tileSize
  const r = ((hasThemeColors ? 5 : 4) / tileSize) * 100;

  return (
    <svg viewBox="0 0 100 100">
      {points &&
        points.map((d) => (
          <Point
            key={getId(d)}
            d={d}
            r={r}
            getId={getId}
            hasThemeColors={hasThemeColors}
            inStory={inStory}
            showTooltip={showTooltip}
            hideTooltip={hideTooltip}
            isCenterTile={isCenterTile}
            isTouch={isTouch}
          />
        ))}

      {isCenterTile && getTooltip && tooltipContext && (
        <Portal>{getTooltip(tooltipContext)}</Portal>
      )}
    </svg>
  );
}

const TouchTarget = styled('circle', {
  fill: 'transparent',
});

const Circle = styled('circle', {
  pointerEvents: 'none',

  variants: {
    hasThemeColors: {
      true: {
        fill: 'var(--color-primary)',
      },
      false: {
        fill: '$white',
      },
    },
  },
});

const FocusRing = styled('circle', {
  stroke: 'none',
  strokeWidth: '0.2',
  fill: 'none',
  transition: 'stroke 0.1s linear',

  'a:focus &': {
    strokeWidth: '0.4',
    strokeDasharray: '0.2 1',
    strokeLinecap: 'round',
  },

  variants: {
    hasThemeColors: {
      true: {
        'a:hover &, a:focus &': {
          stroke: 'var(--color-primary)',
        },
      },
      false: {
        'a:hover &, a:focus &': {
          stroke: '$white',
        },
      },
    },
  },
});

const Anchor = styled('a', {
  cursor: 'pointer',
  '&:focus': {
    outline: 'none',
  },
});

type PointProps<T> = {
  d: CountryCoordinate & T;
  getId: (d: T) => CountryIsoCode;
  showTooltip: (e: FocusEvent | PointerEvent, d: CountryCoordinate & T) => void;
  hideTooltip: (e: FocusEvent | PointerEvent, d: CountryCoordinate & T) => void;
  hasThemeColors?: boolean;
  inStory?: boolean;
  isCenterTile?: boolean;
  isTouch: boolean;
  r: number;
};

function Point<T>(props: PointProps<T>) {
  const {
    d,
    r,
    getId,
    showTooltip,
    hideTooltip,
    hasThemeColors = false,
    inStory,
    isCenterTile,
    isTouch,
  } = props;

  const [x, y] = d.pos;

  if (isCenterTile) {
    return (
      <Anchor
        key={getId(d)}
        href={`#${getId(d)}`}
        onFocus={(e) => showTooltip(e, d)}
        onPointerDown={isTouch ? (e) => showTooltip(e, d) : undefined}
        onPointerEnter={isTouch ? undefined : (e) => showTooltip(e, d)}
        onPointerLeave={isTouch ? undefined : (e) => hideTooltip(e, d)}
        onBlur={(e) => hideTooltip(e, d)}
        aria-label={countryIdToName(getId(d))}
      >
        <TouchTarget r={r * 3} cx={x} cy={y} />
        <Circle
          r={r}
          cx={x}
          cy={y}
          hasThemeColors={hasThemeColors}
          css={{
            fillOpacity: inStory ? 1.0 : 0.8,
          }}
        />
        <FocusRing r={r * 3} cx={x} cy={y} hasThemeColors={hasThemeColors} />
      </Anchor>
    );
  }

  return (
    <Circle
      r={r}
      cx={x}
      cy={y}
      hasThemeColors={hasThemeColors}
      css={{
        fillOpacity: 0.6,
      }}
    />
  );
}
